<template>
    <div
        v-if="showSessionCountdown"
        ref="modalBackdrop"
        class="fixed inset-0 z-9999 flex items-center justify-center bg-black/60"
    >
        <div
            ref="focusTrapContainer"
            role="dialog"
            aria-modal="true"
            :aria-label="localizations[lang].sessionExpiredAlert"
            class="m-auto flex h-auto w-[90vw] max-w-[600px] items-center justify-center rounded bg-theme-bg px-10 shadow-lg sm:max-w-[480px] md:max-w-[600px] lg:w-[480px]"
        >
            <div
                ref="modalContent"
                class="-mx-5 my-0 flex h-full w-full flex-col text-lg leading-relaxed text-theme-text transition-[font-size] duration-100 ease-in-out"
            >
                <div class="mx-auto flex items-center justify-center px-0">
                    <div class="mb-7 flex w-full flex-col justify-center md:mt-0">
                        <div
                            v-if="!showSessionExpiredButton"
                            role="alert"
                            aria-live="assertive"
                            class="w-full pt-3 md:w-full"
                        >
                            <div class="mb-4 mt-5 w-full px-0 py-4 text-center md:mb-2 md:mt-2.5 md:w-full">
                                <p class="mb-0">{{ localizations[lang].sessionExpiredAlert }}</p>
                                <p class="mb-0 pt-2 font-medium">{{ localizations[lang].sessionExpiredAreYouThere }}</p>
                            </div>

                            <div class="my-3 flex w-full justify-center md:w-full">
                                <div class="relative w-64 text-center">
                                    <div
                                        class="absolute inset-x-0 top-0 z-10 h-1.5 rounded bg-theme-accent transition-[width,background-color] duration-300 ease-linear"
                                        role="progressbar"
                                        :style="{ width: progressBarWidth + '%' }"
                                        :aria-valuenow="progressBarWidth"
                                        aria-valuemin="0"
                                        aria-valuemax="100"
                                    ></div>
                                    <div
                                        class="absolute inset-x-0 top-0 z-0 h-1.5 w-full rounded bg-theme-progress-track"
                                    ></div>
                                </div>
                            </div>
                            <!-- 
                                Using arbitrary breakpoint [min-width:416px] instead of Tailwind's default breakpoints 
                                (sm:640px, md:768px) to match the original SCSS design which switched from stacked to 
                                side-by-side layout at 415px. This ensures screens between 416px-767px display buttons 
                                side-by-side as intended, rather than stacked until 768px.
                            -->
                            <div
                                class="[min-width:416px]:grid-cols-2 mb-3 mt-4 grid w-full grid-cols-1 gap-4 px-0 py-2 text-sm"
                            >
                                <button
                                    class="flatButtonTw [min-width:416px]:order-1 order-2 my-2"
                                    @click.prevent="closeVolpe"
                                >
                                    {{ localizations[lang].returnToLibrary }}
                                </button>

                                <button
                                    class="linearButtonTw [min-width:416px]:order-2 order-1 my-2"
                                    @click.prevent="stopCountdown"
                                >
                                    {{ localizations[lang].imHere }}
                                </button>
                            </div>
                        </div>
                        <div v-else role="alert" aria-live="assertive" class="mt-0 flex w-full flex-col justify-center">
                            <div class="w-full py-2 text-center">
                                <svg-icon icon="bookzzz" width="62px" height="62px"></svg-icon>
                            </div>
                            <div class="mb-4 mt-5 w-full py-2 text-center md:mb-2 md:mt-2.5 md:w-full">
                                <p class="mb-0">{{ localizations[lang].sessionExpired }}</p>
                            </div>
                            <div class="flex w-full items-center justify-center py-2 text-sm md:w-full">
                                <button class="linearButtonTw mb-3" @click.prevent="closeVolpe">
                                    {{ localizations[lang].returnToLibrary }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import isTouchDevice from '_shared_/js/utils/isTouchDevice.js';
import { uiAPI } from '../../../src/js/communication';
import focusTrapMixin from '../../js/mixins/focusTrapMixin';

/**
 * This constants are tied up to the PurgeExpiredSessions job
 * in Coniglio. If these change, the ones in Coniglio
 * musr change too, or sessions will miss behave.
 *
 * Total allowed time of inactivity is 18 minutes.
 */
// 15 minutes
const MAX_SECONDS_OF_INACTIVITY = 900;
// 3 minutes
const MAX_SECONDS_OF_COUNTDOWN = 180;

export default {
    name: 'SessionCountdown',
    mixins: [focusTrapMixin],
    data() {
        return {
            showSessionExpiredButton: false,
            secondsOfInactivity: 0,
            countdownInterval: null,
            countdownSeconds: 0,
            focusTrapEnabled: false,
        };
    },
    computed: {
        ...mapState(['concurrencyLimit', 'lang', 'localizations']),
        maxSecondsOfInactivity() {
            return this.concurrencyLimit.debug ? 10 : MAX_SECONDS_OF_INACTIVITY;
        },
        maxSecondsOfCountdown() {
            return this.concurrencyLimit.debug ? 5 : MAX_SECONDS_OF_COUNTDOWN;
        },
        showSessionCountdown() {
            return this.secondsOfInactivity > this.maxSecondsOfInactivity;
        },
        progressBarWidth() {
            return this.countdownSeconds / 1.8;
        },
    },
    watch: {
        showSessionCountdown: {
            immediate: true,
            handler(showSessionCountdown) {
                this.focusTrapEnabled = showSessionCountdown;
                if (showSessionCountdown) {
                    this.startCountdown();
                }
            },
        },
    },
    mounted() {
        setInterval(() => {
            this.secondsOfInactivity++;
        }, 1000);
        document.body.addEventListener(isTouchDevice ? 'touchend' : 'click', this.resetSecondsOfInactivity);
        document.addEventListener('keydown', this.resetSecondsOfInactivity);
    },
    methods: {
        onFocusTrapEscape() {
            // If countdown is active, pressing ESC confirms user presence
            // If session already expired, close the reader
            if (this.showSessionExpiredButton) {
                this.closeVolpe();
            } else {
                this.stopCountdown();
            }
        },
        closeVolpe() {
            uiAPI.close();
        },
        resetSecondsOfInactivity() {
            if (!this.showSessionCountdown) {
                this.secondsOfInactivity = 0;
            }
        },
        startCountdown() {
            this.countdownInterval = setInterval(() => {
                this.countdownSeconds++;
                if (this.countdownSeconds === this.maxSecondsOfCountdown) {
                    clearInterval(this.countdownInterval);
                    this.showSessionExpiredButton = true;
                    // this.$bus.$emit('coniglio.session-expired');
                }
            }, 1000);
        },
        stopCountdown() {
            clearInterval(this.countdownInterval);
            this.countdownSeconds = 0;
            this.secondsOfInactivity = 0;
        },
    },
};
</script>
